'use client';

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import { useRouter } from 'next/navigation';
import { useWorkspace } from '@/contexts/WorkspaceProvider';
import {
  getActiveAnnouncements, getPanelFeed, dismissAnnouncement, markAllAnnouncementsRead,
  trackAnnouncementCtaClick, type Announcement,
} from '@/services/announcements';

interface AnnouncementsContextValue {
  /** Undismissed active announcements (strip + toast placements). */
  active: Announcement[];
  /** Full "What's new" feed incl. dismissed history, with per-row `seen`. */
  panelFeed: Announcement[];
  unreadCount: number;
  lightboxAnnouncement: Announcement | null;
  openLightbox: (a: Announcement) => void;
  closeLightbox: () => void;
  dismiss: (id: number) => void;
  markAllRead: () => void;
  /** Full CTA handler: tracks the click, then opens the lightbox (video+lightbox) or navigates. */
  handleCta: (a: Announcement) => void;
  refresh: () => void;
}

const AnnouncementsContext = createContext<AnnouncementsContextValue | undefined>(undefined);

export const AnnouncementsProvider = ({ children }: { children: ReactNode }) => {
  const workspace = useWorkspace();
  const router = useRouter();
  const vendorId = workspace?.activeWorkspace?.vendorid ?? null;

  const [active, setActive] = useState<Announcement[]>([]);
  const [panelFeed, setPanelFeed] = useState<Announcement[]>([]);
  const [lightboxAnnouncement, setLightboxAnnouncement] = useState<Announcement | null>(null);

  const load = useCallback(() => {
    if (!vendorId) {
      setActive([]);
      setPanelFeed([]);
      return;
    }
    // Announcements are non-critical chrome — never block the app on a fetch error.
    getActiveAnnouncements().then(setActive).catch(() => setActive([]));
    getPanelFeed().then(setPanelFeed).catch(() => setPanelFeed([]));
  }, [vendorId]);

  useEffect(() => {
    load();
  }, [load]);

  const unreadCount = useMemo(() => panelFeed.filter((a) => !a.seen).length, [panelFeed]);

  const dismiss = useCallback((id: number) => {
    setActive((prev) => prev.filter((a) => a.id !== id));
    dismissAnnouncement(id).catch(() => {});
  }, []);

  const markAllRead = useCallback(() => {
    setPanelFeed((prev) => prev.map((a) => ({ ...a, seen: true })));
    markAllAnnouncementsRead().catch(() => {});
  }, []);

  const openLightbox = useCallback((a: Announcement) => setLightboxAnnouncement(a), []);
  const closeLightbox = useCallback(() => setLightboxAnnouncement(null), []);

  const handleCta = useCallback((a: Announcement) => {
    trackAnnouncementCtaClick(a.id).catch(() => {});
    if (a.media_type === 'video' && a.video_mode === 'lightbox' && a.video_url) {
      setLightboxAnnouncement(a);
      return;
    }
    const target = a.media_type === 'video' ? a.video_url : a.cta_link;
    if (!target) return;
    // Internal route only for single-leading-slash paths; '//host' is a protocol-relative
    // external URL and must go through the new-tab/noopener path, not router.push.
    if (target.startsWith('/') && !target.startsWith('//')) router.push(target);
    else window.open(target, '_blank', 'noopener,noreferrer');
  }, [router]);

  const value = useMemo<AnnouncementsContextValue>(() => ({
    active,
    panelFeed,
    unreadCount,
    lightboxAnnouncement,
    openLightbox,
    closeLightbox,
    dismiss,
    markAllRead,
    handleCta,
    refresh: load,
  }), [active, panelFeed, unreadCount, lightboxAnnouncement, openLightbox, closeLightbox, dismiss, markAllRead, handleCta, load]);

  return <AnnouncementsContext.Provider value={value}>{children}</AnnouncementsContext.Provider>;
};

export const useAnnouncements = (): AnnouncementsContextValue => {
  const ctx = useContext(AnnouncementsContext);
  if (!ctx) {
    throw new Error('useAnnouncements must be used within AnnouncementsProvider');
  }
  return ctx;
};
